import {
  DirectLoadSortBySchema,
  type DirectLoadSortBy,
  type DirectLoadStatus,
  type ListDirectLoadsInput,
} from '@erp/rpc';
import type { ServerSort } from '@/components/common/data-table/server-sort';
import type { ColumnFiltersState } from '@/lib/tanstack-table';

export const DEFAULT_LOADS_PAGE_SIZE = 25;

/** The loads table's status column id; its facet filters on it. */
export const LOAD_STATUS_COLUMN_ID = 'status';

export const LOAD_STATUSES: DirectLoadStatus[] = [
  'running',
  'succeeded',
  'partial',
  'failed',
  'cancelled',
];

const LOAD_STATUS_SET = new Set<string>(LOAD_STATUSES);

export function isLoadStatus(value: unknown): value is DirectLoadStatus {
  return typeof value === 'string' && LOAD_STATUS_SET.has(value);
}

/** What the loads table's toolbar, headers and pager hold: one value, whichever side applies it. */
export interface LoadsQuery {
  pageIndex: number;
  pageSize: number;
  /** The primary sort column and its tie-breakers, as the server takes them. */
  sort: ServerSort<DirectLoadSortBy>;
  search: string;
  statuses: DirectLoadStatus[];
}

export const DEFAULT_LOADS_QUERY: LoadsQuery = {
  pageIndex: 0,
  pageSize: DEFAULT_LOADS_PAGE_SIZE,
  sort: {},
  search: '',
  statuses: [],
};

/** Whether the query narrows the list at all (paging and sorting do not). */
export function loadsQueryFilters(query: LoadsQuery): boolean {
  return query.search.trim() !== '' || query.statuses.length > 0;
}

export function toColumnFilters(
  statuses: DirectLoadStatus[],
): ColumnFiltersState {
  return statuses.length > 0
    ? [{ id: LOAD_STATUS_COLUMN_ID, value: statuses }]
    : [];
}

export function toStatuses(filters: ColumnFiltersState): DirectLoadStatus[] {
  const value = filters.find((f) => f.id === LOAD_STATUS_COLUMN_ID)?.value;
  return Array.isArray(value) ? value.filter(isLoadStatus) : [];
}

/** The server sort key for a loads-table column id, if the server sorts by it. */
export function toDirectLoadSortBy(id: string): DirectLoadSortBy | undefined {
  return DirectLoadSortBySchema.safeParse(id).data;
}

/** The server's page request for the table's query. */
export function toListDirectLoadsInput(
  query: LoadsQuery,
): ListDirectLoadsInput {
  const { sortBy, sortDirection, thenSort } = query.sort;
  const search = query.search.trim();
  return {
    limit: query.pageSize,
    offset: query.pageIndex * query.pageSize,
    ...(search !== '' ? { search } : {}),
    ...(query.statuses.length > 0 ? { status: query.statuses } : {}),
    ...(sortBy
      ? { sortBy, sortDirection, ...(thenSort?.length ? { thenSort } : {}) }
      : {}),
  };
}
